import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Header from '../../components/common/global-top-bar';
import SwitchBar from '../../components/switch-bar';
import Spinner from '../../components/common/spinner';
import { PERSONA_TABS } from './persona-constants';
import {
  Persona,
  usePersonas,
  useCreatePersona,
  usePublishPersona,
  useUnpublishPersona,
} from '../../hooks/usePersonaAdmin';
import CreatePersonaDialog from './create-persona-dialog';

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case 'on':
      return 'bg-green-100 text-green-800';
    case 'off':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const processingBadgeClass = (status?: string) => {
  switch (status) {
    case 'ready':
      return 'bg-blue-100 text-blue-800';
    case 'processing':
      return 'bg-amber-100 text-amber-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const PersonaRow: React.FC<{ persona: Persona }> = ({ persona }) => {
  const history = useHistory();
  const publish = usePublishPersona(persona.id);
  const unpublish = useUnpublishPersona(persona.id);

  const goToDetail = () => history.push(`/personas/${persona.id}/overview`);

  const isOn = persona.status === 'on';

  return (
    <tr
      className="cursor-pointer border-b text-sm hover:bg-gray-50"
      onClick={goToDetail}
      data-testid={`persona-row-${persona.id}`}
    >
      <td className="py-3 pl-4 pr-2">
        <div className="font-medium text-gray-900">{persona.display_name || persona.name}</div>
        <div className="text-xs text-gray-500">{persona.name}</div>
      </td>
      <td className="py-3 pr-2">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(persona.status)}`}
        >
          {persona.status || 'off'}
        </span>
      </td>
      <td className="py-3 pr-2">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${processingBadgeClass(
            persona.processing_status,
          )}`}
        >
          {persona.processing_status || 'pending'}
        </span>
      </td>
      <td className="py-3 pr-2 text-sm text-gray-700">{persona.chunk_count ?? 0}</td>
      <td className="py-3 pr-2 text-sm text-gray-700">{persona.assigned_client_count ?? 0}</td>
      <td className="py-3 pr-4 text-right" onClick={(e) => e.stopPropagation()}>
        {isOn ? (
          <button
            className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            disabled={unpublish.isLoading}
            onClick={() => unpublish.mutate()}
          >
            {unpublish.isLoading ? 'Unpublishing…' : 'Unpublish'}
          </button>
        ) : (
          <button
            className="rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
            disabled={publish.isLoading || persona.processing_status !== 'ready'}
            title={persona.processing_status !== 'ready' ? 'Corpus must be ready before publishing' : undefined}
            onClick={() => publish.mutate()}
          >
            {publish.isLoading ? 'Publishing…' : 'Publish'}
          </button>
        )}
        <button
          className="ml-2 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
          onClick={goToDetail}
        >
          Open
        </button>
      </td>
    </tr>
  );
};

const PersonasCatalog: React.FC = () => {
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading } = usePersonas(true);
  const createMutation = useCreatePersona();

  const personas = data?.data || [];

  return (
    <div>
      <Header heading="Personas" />
      <SwitchBar heading={PERSONA_TABS} position={0} />

      <div className="flex items-center justify-between border-b px-7 py-3">
        <span className="text-sm text-gray-500">
          {personas.length} persona{personas.length === 1 ? '' : 's'}
        </span>
        <button
          className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          onClick={() => setShowCreate(true)}
          data-testid="persona-create-button"
        >
          New Persona
        </button>
      </div>

      {isLoading && <Spinner />}
      {!isLoading && personas.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <p className="mb-2 text-sm">No personas yet.</p>
          <button className="text-sm font-medium text-blue-600 hover:text-blue-800" onClick={() => setShowCreate(true)}>
            Create the first one
          </button>
        </div>
      )}

      {!isLoading && personas.length > 0 && (
        <div className="max-window-height-table overflow-x-auto overflow-y-auto px-7">
          <table className="table-row-format relative w-full table-fixed border-collapse">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="w-64 py-3 pl-4 pr-2">Persona</th>
                <th className="w-28 py-3 pr-2">Status</th>
                <th className="w-32 py-3 pr-2">Processing</th>
                <th className="w-24 py-3 pr-2">Chunks</th>
                <th className="w-24 py-3 pr-2">Clients</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {personas.map((persona) => (
                <PersonaRow key={persona.id} persona={persona} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreatePersonaDialog
          onClose={() => setShowCreate(false)}
          onSubmit={async (payload) => {
            await createMutation.mutateAsync(payload);
            setShowCreate(false);
          }}
          isSubmitting={createMutation.isLoading}
        />
      )}
    </div>
  );
};

export default PersonasCatalog;
